import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addFreeDaysToPortalSubscription,
  cancelPortalSubscription,
  getPortalSubscriptions,
  PortalSubscription,
} from '@api/customer-portal-api';
import { queryKeys } from '@hooks/queryKeys';
import { normalizeLineOptions } from '@lib/lineOptions';

const withLineOptions = (sub: PortalSubscription): PortalSubscription =>
  sub.line_item
    ? {
        ...sub,
        line_item: {
          ...sub.line_item,
          selected_options: normalizeLineOptions(sub.line_item.selected_options),
        },
      }
    : sub;

export const useSubscriptions = () =>
  useQuery({
    queryKey: queryKeys.subscriptions.all,
    queryFn: async (): Promise<PortalSubscription[]> => {
      const data = await getPortalSubscriptions();
      return Array.isArray(data) ? data.map(withLineOptions) : [];
    },
  });

interface CancelInput {
  subscriptionId: string;
  cancellationMode?: 'at_period_end' | 'immediate';
}

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, cancellationMode = 'at_period_end' }: CancelInput) =>
      cancelPortalSubscription(subscriptionId, cancellationMode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });
    },
  });
};

interface RescheduleInput {
  subscriptionId: string;
  freeDays: number;
}

export const useRescheduleSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriptionId, freeDays }: RescheduleInput) =>
      addFreeDaysToPortalSubscription(subscriptionId, freeDays),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });
    },
  });
};
